import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "./api";
import { lifecycle } from "./lifecycle";

const AuthCtx = createContext({
  user: null, loading: true,
  login: async () => {}, logout: async () => {}, revalidate: async () => {},
});

// Al recovery cross-device (foreground/wake/online) rivalidiamo la sessione
// solo se l'ultima verifica è più vecchia di questo intervallo → evitiamo
// bombardamenti di /auth/me su switch di tab veloci.
const REVALIDATE_MIN_INTERVAL_MS = 60_000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastCheckRef = useRef(0);

  const revalidate = async () => {
    // Se il tab è offline, non chiamiamo /auth/me (fallirebbe di rete e
    // introdurrebbe false-positive di sessione persa).
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      lastCheckRef.current = Date.now();
    } catch (e) {
      // 401 già gestito dall'interceptor api.js (refresh → sb-auth-lost su
      // fallimento definitivo). Errori transienti (rete/5xx) NON logoutano.
      // silent
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        lastCheckRef.current = Date.now();
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Cross-device recovery: al ritorno operativo, se l'ultima verifica è
  // vecchia > REVALIDATE_MIN_INTERVAL_MS, rivalidiamo la sessione in
  // silenzio. Il 401 → refresh → sb-auth-lost flow è gestito da api.js.
  useEffect(() => {
    const off = lifecycle.on("recovery", () => {
      const age = Date.now() - lastCheckRef.current;
      if (age >= REVALIDATE_MIN_INTERVAL_MS && user) {
        revalidate();
      }
    });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem("sb_token", data.access_token);
    // Rifetch full user via /auth/me to include effective_permissions
    // (login endpoint returns only base fields → AdminGuard needs perms).
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      lastCheckRef.current = Date.now();
      return me.data;
    } catch {
      setUser(data);
      return data;
    }
  };

  const pinLogin = async (pin) => {
    const { data } = await api.post("/auth/pin-login", { pin });
    if (data.access_token) localStorage.setItem("sb_token", data.access_token);
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      lastCheckRef.current = Date.now();
      return me.data;
    } catch {
      setUser(data);
      return data;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("sb_token");
    setUser(null);
    lastCheckRef.current = 0;
  };

  return <AuthCtx.Provider value={{ user, loading, login, pinLogin, logout, revalidate, setUser }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

export const ROLE_LEVEL = {
  super_admin: 100,
  admin: 80,
  manager: 60,
  bartender: 40,
  bar: 40,
  sala: 40,
  cucina: 40,
  spiaggia: 40,
  runner: 30,
  analyst: 20,
  // meta_reviewer: ruolo dedicato al reviewer Meta App Review — livello 15.
  // < analyst (20) → tutti i require_role(...,"analyst") ritornano 403.
  meta_reviewer: 15,
};
export const canAccess = (user, minRole) => (user && ROLE_LEVEL[user.role] >= ROLE_LEVEL[minRole]);
